// pages/WelcomeBonus.js - Welcome Bonus promotions page with table layout
import React, { useState, useMemo, useEffect } from 'react';
import { Gift, ChevronUp, ChevronDown, Eye, Flag, X, Coins } from 'lucide-react';
import promotionService from '../services/PromotionService';
import reportService from '../services/ReportService';
import { useAuth } from '../context/AuthContext';
import {
  ALL_GAME_CATEGORIES,
  formatGameCategories,
  getCategoryIcon,
  getCategoryColor
} from '../lib/gameCategory';

function WelcomeBonusPage() {
  const { user } = useAuth();

  // State for sorting
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // State for description popup
  const [selectedPromotion, setSelectedPromotion] = useState(null);

  // State for report modal
  const [reportModal, setReportModal] = useState({
    isOpen: false,
    promotion: null,
    issueType: '',
    description: '',
    submitting: false
  });

  // State for reward notification
  const [showRewardNotification, setShowRewardNotification] = useState(false);

  // State for data loading
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // All available game categories (from library)
  const allGameCategories = ALL_GAME_CATEGORIES;

  // Fetch real welcome bonus promotions from database
  useEffect(() => {
    const fetchWelcomeBonusPromotions = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all promotions from the service
        const result = await promotionService.fetchAllPromotions();

        if (result.success) {
          // Filter for only "Welcome Bonus" category promotions
          const welcomeBonusPromotions = result.data.promotions.filter(promotion =>
            promotion.category && promotion.category.toLowerCase() === 'welcome bonus'
          );

          // Transform data to ensure consistent field names
          const transformedPromotions = welcomeBonusPromotions.map(promotion => ({
            ...promotion,
            // Ensure we have the required fields with fallbacks
            maxBonus: promotion.maxBonus || 0,
            turnoverMultiplier: promotion.turnoverNeeded || promotion.turnoverMultiplier || 0,
            gameCategories: promotion.gameCategories || [],
            brandLogo: promotion.brandLogo || null,
            brandName: promotion.brandName || 'Unknown Brand',
            title: promotion.title || 'Welcome Bonus',
            description: promotion.description || 'No description available'
          }));

          setPromotions(transformedPromotions);
        } else {
          setError(result.error || 'Failed to load welcome bonus promotions');
          setPromotions([]);
        }
      } catch (err) {
        console.error('Error fetching welcome bonus promotions:', err);
        setError('Failed to load welcome bonus promotions');
        setPromotions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchWelcomeBonusPromotions();
  }, []);

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Use the library function for formatting game categories

  // Filter and sort data
  const sortedData = useMemo(() => {
    // First filter to show only promotions with maxBonus or turnover not null/zero
    const filteredPromotions = promotions.filter(promotion =>
      (promotion.maxBonus && promotion.maxBonus > 0) ||
      (promotion.turnoverMultiplier && promotion.turnoverMultiplier > 0)
    );

    if (!sortField) return filteredPromotions;

    return [...filteredPromotions].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle numeric sorting
      if (sortField === 'maxBonus' || sortField === 'turnoverMultiplier') {
        aValue = Number(aValue);
        bValue = Number(bValue);
      }

      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  }, [promotions, sortField, sortDirection]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Format currency with k notation for mobile
  const formatCurrencyCompact = (amount) => {
    if (amount >= 1000000) {
      return `Rp${(amount / 1000000).toFixed(0)}M`;
    } else if (amount >= 1000) {
      return `Rp${(amount / 1000).toFixed(0)}k`;
    }
    return `Rp${amount}`;
  };

  // Render sort icon
  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return <ChevronUp className="w-4 h-4 text-gray-400" />;
    }
    return sortDirection === 'asc' ?
      <ChevronUp className="w-4 h-4 text-blue-600" /> :
      <ChevronDown className="w-4 h-4 text-blue-600" />;
  };

  // Convert markdown-like formatting to HTML for display
  const formatDescription = (text) => {
    if (!text) return '';

    return text
      .replace(/^## (.+)$/gm, '<h2 class="text-lg font-bold text-blue-800 mb-2 mt-2">$1</h2>')
      .replace(/^### (.+)$/gm, '<h3 class="text-base font-semibold text-blue-700 mb-1 mt-2">$1</h3>')
      .replace(/\*\*(.+?)\*\*/g, '<strong class="font-bold text-blue-800">$1</strong>')
      .replace(/\n/g, '<br>');
  };

  // Handle description popup
  const openDescriptionPopup = (promotion) => {
    setSelectedPromotion(promotion);
    document.body.style.overflow = 'hidden';
  };

  const closeDescriptionPopup = () => {
    setSelectedPromotion(null);
    document.body.style.overflow = 'auto';
  };

  // Handle report modal
  const openReportModal = (promotion) => {
    setReportModal({
      isOpen: true,
      promotion,
      issueType: '',
      description: '',
      submitting: false
    });
    document.body.style.overflow = 'hidden';
  };

  const closeReportModal = () => {
    setReportModal({
      isOpen: false,
      promotion: null,
      issueType: '',
      description: '',
      submitting: false
    });
    document.body.style.overflow = 'auto';
  };

  const handleReportSubmit = async () => {
    if (!reportModal.description.trim()) {
      alert('Please provide a description of the issue.');
      return;
    }

    if (!user) {
      alert('Please log in to submit a report.');
      return;
    }

    setReportModal(prev => ({ ...prev, submitting: true }));

    try {
      const reportData = {
        userId: user.uid,
        userEmail: user.email,
        promotionId: reportModal.promotion.id,
        promotionTitle: reportModal.promotion.title,
        brandId: reportModal.promotion.brandId,
        brandName: reportModal.promotion.brandName,
        description: reportModal.description.trim(),
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      };

      const result = await reportService.submitReport(reportData);

      if (result.success) {
        closeReportModal();
        setShowRewardNotification(true);
        // Auto-hide notification after 5 seconds
        setTimeout(() => setShowRewardNotification(false), 5000);
      } else {
        alert('Failed to submit report. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting report:', error);
      alert('Failed to submit report. Please try again.');
    } finally {
      setReportModal(prev => ({ ...prev, submitting: false }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white py-4 md:py-8">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold flex items-center">
                <Gift className="mr-2 md:mr-3" size={24} />
                Welcome Bonus
              </h1>
              <p className="mt-1 md:mt-2 text-green-100 text-sm md:text-base">
                Discover the best welcome bonus promotions from top brands
              </p>
            </div>
            <div className="hidden md:block text-right">
              <div className="text-green-100 text-sm">
                Total Promotions: <span className="font-bold text-white">{sortedData.length}</span>
              </div>
              <div className="text-green-200 text-xs mt-1">
                Updated: {new Date().toLocaleTimeString()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="container mx-auto px-4 py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading welcome bonus promotions...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="container mx-auto px-4 py-12">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <Gift className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-red-800 mb-2">Failed to Load Promotions</h3>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && sortedData.length === 0 && (
        <div className="container mx-auto px-4 py-12">
          <div className="text-center">
            <Gift className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No Welcome Bonus Promotions Found</h3>
            <p className="text-gray-500">Check back later for new welcome bonus offers with valid bonus amounts.</p>
          </div>
        </div>
      )}

      {/* Content */}
      {!loading && !error && sortedData.length > 0 && (
        <div className="container mx-auto px-4 py-4">
          {/* Desktop Table - Hidden on mobile */}
          <div className="hidden md:block bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Brand
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('maxBonus')}
                    >
                      <div className="flex items-center">
                        Max Bonus
                        {renderSortIcon('maxBonus')}
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('turnoverMultiplier')}
                    >
                      <div className="flex items-center">
                        Turnover
                        {renderSortIcon('turnoverMultiplier')}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Game Category
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedData.map((promotion) => (
                    <tr key={promotion.id} className="hover:bg-gray-50">
                      {/* Brand (Logo + Name) */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <img
                              src={promotion.brandLogo}
                              alt={promotion.brandName}
                              className="h-10 w-10 rounded-full object-cover border-2 border-gray-200"
                              onError={(e) => {
                                e.target.src = `https://via.placeholder.com/40x40?text=${promotion.brandName.charAt(0)}`;
                              }}
                            />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {promotion.brandName}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Title */}
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {promotion.title}
                        </div>
                      </td>

                      {/* Max Bonus */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-green-600">
                          {formatCurrency(promotion.maxBonus)}
                        </div>
                      </td>

                      {/* Turnover */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-orange-600">
                          {promotion.turnoverMultiplier}x
                        </div>
                      </td>

                      {/* Game Categories */}
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {formatGameCategories(promotion.gameCategories) === 'All' ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              All Games
                            </span>
                          ) : (
                            promotion.gameCategories.map((category, index) => {
                              const IconComponent = getCategoryIcon(category);
                              const colorClass = getCategoryColor(category);
                              return (
                                <span
                                  key={index}
                                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}
                                >
                                  {IconComponent && <IconComponent className="w-3 h-3 mr-1" />}
                                  {category}
                                </span>
                              );
                            })
                          )}
                        </div>
                      </td>

                      {/* Details Button */}
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => openDescriptionPopup(promotion)}
                          className="inline-flex items-center px-3 py-1 border border-blue-300 rounded-full text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Layout - Visible only on mobile */}
          <div className="md:hidden px-0 -mx-4">

            {/* Mobile Table-like List */}
            <div className="bg-white shadow-sm overflow-hidden">
              {/* Mobile Table Header */}
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                <div className="grid grid-cols-10 gap-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="col-span-4">Brand</div>
                  <div
                    className="col-span-2 text-center cursor-pointer hover:bg-gray-100 rounded px-1 py-1 flex items-center justify-center"
                    onClick={() => handleSort('maxBonus')}
                  >
                    Max {renderSortIcon('maxBonus')}
                  </div>
                  <div
                    className="col-span-2 text-center cursor-pointer hover:bg-gray-100 rounded px-1 py-1 flex items-center justify-center"
                    onClick={() => handleSort('turnoverMultiplier')}
                  >
                    TO {renderSortIcon('turnoverMultiplier')}
                  </div>
                  <div className="col-span-2 text-center">View</div>
                </div>
              </div>

              {/* Mobile Table Rows */}
              <div className="divide-y divide-gray-200">
                {sortedData.map((promotion) => (
                  <div
                    key={promotion.id}
                    className="px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => openDescriptionPopup(promotion)}
                  >
                    <div className="grid grid-cols-10 gap-3 items-center">
                      {/* Brand Column */}
                      <div className="col-span-4 flex items-center min-w-0">
                        <div className="flex-shrink-0 h-8 w-8 mr-3">
                          <img
                            src={promotion.brandLogo}
                            alt={promotion.brandName}
                            className="h-8 w-8 rounded-full object-cover border border-gray-200"
                            onError={(e) => {
                              e.target.src = `https://via.placeholder.com/32x32?text=${promotion.brandName.charAt(0)}`;
                            }}
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-medium text-gray-900 truncate">
                            {promotion.brandName}
                          </div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {formatGameCategories(promotion.gameCategories) === 'All' ? (
                              <span className="inline-flex items-center justify-center w-5 h-5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                All
                              </span>
                            ) : (
                              <>
                                {promotion.gameCategories.slice(0, 3).map((category, index) => {
                                  const IconComponent = getCategoryIcon(category);
                                  const colorClass = getCategoryColor(category);
                                  return (
                                    <span
                                      key={index}
                                      className={`inline-flex items-center justify-center w-5 h-5 rounded ${colorClass}`}
                                      title={category}
                                    >
                                      {IconComponent && <IconComponent className="w-3 h-3" />}
                                    </span>
                                  );
                                })}
                                {promotion.gameCategories.length > 3 && (
                                  <span className="inline-flex items-center justify-center w-5 h-5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                                    +{promotion.gameCategories.length - 3}
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Max Bonus Column */}
                      <div className="col-span-2 text-center">
                        <div className="text-xs font-bold text-green-600">
                          {formatCurrencyCompact(promotion.maxBonus)}
                        </div>
                      </div>

                      {/* Turnover Column */}
                      <div className="col-span-2 text-center">
                        <div className="text-xs font-bold text-orange-600">
                          {promotion.turnoverMultiplier}x
                        </div>
                      </div>

                      {/* View Column */}
                      <div className="col-span-2 text-center">
                        <Eye className="w-4 h-4 text-blue-600 mx-auto" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>


        </div>
      )}

      {/* Description Popup */}
      {selectedPromotion && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4"
          onClick={() => closeDescriptionPopup()}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-blue-50">
              <div className="flex items-center">
                <img
                  src={selectedPromotion.brandLogo}
                  alt={selectedPromotion.brandName}
                  className="h-8 w-8 rounded-full object-cover border border-white shadow-sm mr-2"
                  onError={(e) => {
                    e.target.src = `https://via.placeholder.com/32x32?text=${selectedPromotion.brandName.charAt(0)}`;
                  }}
                />
                <div>
                  <div className="text-sm font-medium text-gray-900">{selectedPromotion.brandName}</div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh] bg-gray-50">
              {/* Title */}
              <div className="mb-4">
                <h3 className="text-xl font-bold text-gray-900 text-center">{selectedPromotion.title}</h3>
              </div>

              {/* Quick Info */}
              <div className="grid grid-cols-2 gap-4 mb-6 bg-white rounded-lg p-4 shadow-sm">
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Max Bonus</div>
                  <div className="text-lg font-bold text-green-600">{formatCurrency(selectedPromotion.maxBonus)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Turnover</div>
                  <div className="text-lg font-bold text-orange-600">{selectedPromotion.turnoverMultiplier}x</div>
                </div>
              </div>

              {/* Game Categories */}
              <div className="mb-6">
                <div className="text-sm font-medium text-gray-700 mb-2">Available Game Categories:</div>
                <div className="flex flex-wrap gap-2">
                  {formatGameCategories(selectedPromotion.gameCategories) === 'All' ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                      All Games
                    </span>
                  ) : (
                    selectedPromotion.gameCategories.map((category, index) => {
                      const IconComponent = getCategoryIcon(category);
                      const colorClass = getCategoryColor(category);
                      return (
                        <span
                          key={index}
                          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${colorClass}`}
                        >
                          {IconComponent && <IconComponent className="w-4 h-4 mr-1" />}
                          {category}
                        </span>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Description */}
              <div className="prose prose-sm max-w-none">
                <div
                  dangerouslySetInnerHTML={{
                    __html: formatDescription(selectedPromotion.description)
                  }}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex space-x-3">
                <button
                  onClick={() => openReportModal(selectedPromotion)}
                  className="flex-[2] px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center"
                >
                  Report Wrong Info
                </button>
                <button
                  onClick={() => closeDescriptionPopup()}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report Modal */}
      {reportModal.isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4"
          onClick={() => closeReportModal()}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-red-50 to-orange-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <h3 className="text-lg font-semibold text-gray-900">Report Wrong Information</h3>
                </div>
                <button
                  onClick={() => closeReportModal()}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Reporting issue for: <span className="font-medium">{reportModal.promotion?.title}</span>
                </p>
                <p className="text-xs text-gray-500">
                  Brand: {reportModal.promotion?.brandName}
                </p>
              </div>

              {/* Description */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What's wrong with this promotion? *
                </label>
                <textarea
                  value={reportModal.description}
                  onChange={(e) => setReportModal(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500 resize-none"
                  rows="5"
                  placeholder="Please describe what information is incorrect or misleading. For example: bonus amount, turnover requirements, game categories, expired promotion, etc."
                  required
                />
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                <div className="flex items-center">
                  <Coins className="h-4 w-4 text-green-600 mr-2" />
                  <div className="text-sm text-green-700">
                    <strong>Earn IDR 5,000</strong> when your report is reviewed by our team!
                  </div>
                </div>
              </div>

              <p className="text-xs text-gray-500">
                Your report will be reviewed by our team. Thank you for helping us maintain accurate information.
              </p>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex space-x-3">
                <button
                  onClick={() => closeReportModal()}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                  disabled={reportModal.submitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleReportSubmit}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={reportModal.submitting || !reportModal.description.trim()}
                >
                  {reportModal.submitting ? 'Submitting...' : 'Submit Report'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reward Notification */}
      {showRewardNotification && (
        <div className="fixed top-4 right-4 z-50 max-w-sm">
          <div className="bg-green-500 text-white rounded-lg shadow-lg p-4 transform transition-all duration-300 ease-in-out">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Coins className="h-6 w-6 text-green-200" />
              </div>
              <div className="ml-3 flex-1">
                <div className="text-sm font-medium">
                  Report Submitted Successfully!
                </div>
                <div className="text-xs text-green-200 mt-1">
                  You'll earn IDR 5,000 when your report is reviewed by our team.
                </div>
              </div>
              <button
                onClick={() => setShowRewardNotification(false)}
                className="ml-3 flex-shrink-0 text-green-200 hover:text-white transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WelcomeBonusPage;